/**
 * Capacity, as every surface is allowed to talk about it.
 *
 * One household sees one number, and this module is where that number is
 * defined once so the web page, the phone and the server cannot each round it
 * differently. Two things are deliberately here rather than in a screen:
 *
 *  - **The gigabyte.** 1 GB is 1,000,000,000 bytes, never 2^30. A customer who
 *    bought "50 GB" and is told they have 46.6 has been shown a different unit
 *    than the one they were sold, and every storage complaint in the industry
 *    starts there.
 *  - **The thresholds.** 70 / 85 / 95 / 100 are the four states the commercial
 *    document names, and only the last one blocks anything. A screen that
 *    invents a fifth, or that blocks at 95, is not the product that was
 *    approved.
 *
 * Nothing here holds a price. Prices live in `pricing.ts`, which is checked
 * against `docs/product/pricing.md` by a test that parses the document.
 */
import { z } from 'zod';
import { EntityId, IsoDatetime } from './primitives.js';

/** 1 GB = 1,000,000,000 bytes. Customer-facing capacity is never GiB. */
export const BYTES_PER_GB = 1_000_000_000 as const;

export function gbToBytes(gb: number): number {
  return Math.round(gb * BYTES_PER_GB);
}

/** For display only. Rounds to one decimal, the way a capacity bar reads. */
export function bytesToGb(bytes: number): number {
  return Math.round((bytes / BYTES_PER_GB) * 10) / 10;
}

export const StorageGrantKind = z.enum(['included_plan', 'storage_box']);
export type StorageGrantKind = z.infer<typeof StorageGrantKind>;

export const StorageGrantState = z.enum(['active', 'grace', 'recovery', 'expired', 'purged']);
export type StorageGrantState = z.infer<typeof StorageGrantState>;

export const BillingChannel = z.enum(['stripe', 'apple', 'google', 'internal']);
export type BillingChannel = z.infer<typeof BillingChannel>;

export const StorageCadence = z.enum(['monthly', 'annual']);
export type StorageCadence = z.infer<typeof StorageCadence>;

/**
 * The four usage states, and what each one is allowed to do.
 *
 * Written as data because three surfaces have to agree on them and because
 * "blocks" is the field that must never quietly become true below 100. When
 * one allowance is exhausted the product pauses that capability and leaves
 * manual task management working; the same rule governs storage, which is why
 * read, download, export, cleanup and billing stay available at 100%.
 */
export interface StorageThreshold {
  percent: 70 | 85 | 95 | 100;
  /** Does crossing this stop new byte-producing actions? */
  blocksNewBytes: boolean;
  /** Owner-only, and persistent rather than dismissible? */
  ownerOnly: boolean;
  persistent: boolean;
}

export const STORAGE_THRESHOLDS: readonly StorageThreshold[] = [
  { percent: 70, blocksNewBytes: false, ownerOnly: false, persistent: false },
  { percent: 85, blocksNewBytes: false, ownerOnly: true, persistent: true },
  { percent: 95, blocksNewBytes: false, ownerOnly: true, persistent: true },
  { percent: 100, blocksNewBytes: true, ownerOnly: true, persistent: true },
] as const;

/** The highest threshold this usage has reached, or null below 70%. */
export function thresholdFor(usedBytes: number, capacityBytes: number): StorageThreshold | null {
  if (capacityBytes <= 0) return usedBytes > 0 ? (STORAGE_THRESHOLDS[3] as StorageThreshold) : null;
  const percent = (usedBytes / capacityBytes) * 100;
  let hit: StorageThreshold | null = null;
  for (const t of STORAGE_THRESHOLDS) if (percent >= t.percent) hit = t;
  return hit;
}

/**
 * One grant as a client may see it.
 *
 * Grant ids are exposed only where the commercial document requires them — a
 * cancellation preflight and the Recovery Vault. Normal Vault browsing must
 * not show internal fragmentation, so a list screen renders the total and
 * never these rows.
 */
export const StorageGrantView = z.object({
  id: EntityId.storageGrant,
  kind: StorageGrantKind,
  capacity_bytes: z.number().int().nonnegative(),
  ordinal: z.number().int().nonnegative(),
  state: StorageGrantState,
  channel: BillingChannel,
  cadence: StorageCadence.nullable(),
  logical_sku: z.string().max(64).nullable(),
  paid_through_at: IsoDatetime.nullable(),
  recovery_deadline_at: IsoDatetime.nullable(),
});
export type StorageGrantView = z.infer<typeof StorageGrantView>;

/**
 * `GET /v1/vault/usage`. The one shape every capacity bar reads.
 *
 * `legacy_overage_bytes` is separate from `used_bytes` because bytes that
 * predate the allowance are stored, visible and exportable while entitling
 * nothing — folding them into the used figure would show a household as over
 * capacity for content we chose to adopt rather than refuse.
 *
 * `system_languages_bytes` is stated so the screen can show language packs as
 * included and non-billable rather than leaving a customer to wonder where
 * their space went.
 */
export const StorageUsage = z.object({
  used_bytes: z.number().int().nonnegative(),
  capacity_bytes: z.number().int().nonnegative(),
  reserved_bytes: z.number().int().nonnegative(),
  trash_bytes: z.number().int().nonnegative(),
  legacy_overage_bytes: z.number().int().nonnegative(),
  recovery_bytes: z.number().int().nonnegative(),
  system_languages_bytes: z.number().int().nonnegative(),
  percent_used: z.number().min(0),
  blocks_new_bytes: z.boolean(),
  threshold: z.union([z.literal(70), z.literal(85), z.literal(95), z.literal(100)]).nullable(),
  grants: z.array(StorageGrantView),
});
export type StorageUsage = z.infer<typeof StorageUsage>;

/**
 * What must happen before a box can end, in exact files.
 *
 * "Export without deletion does not free capacity" is the sentence this shape
 * exists to make unavoidable: `deficit_bytes` stays positive until content is
 * actually removed, and `can_cancel` is false while it is.
 */
export const StorageFitPreflight = z.object({
  can_cancel: z.boolean(),
  surviving_capacity_bytes: z.number().int().nonnegative(),
  retained_bytes: z.number().int().nonnegative(),
  deficit_bytes: z.number().int().nonnegative(),
  affected_grant_id: EntityId.storageGrant.nullable(),
  recovery_deadline_at: IsoDatetime.nullable(),
  affected_documents: z.array(
    z.object({
      document_id: EntityId.document,
      title: z.string(),
      bytes: z.number().int().nonnegative(),
    }),
  ),
});
export type StorageFitPreflight = z.infer<typeof StorageFitPreflight>;

/**
 * The refusals a byte-producing action can meet, as codes rather than prose.
 *
 * A client has to branch on these — quota full offers cleanup and upgrade, a
 * scan failure offers nothing but waiting — and a translated sentence is not
 * something to branch on.
 */
export const StorageErrorCode = z.enum([
  'QUOTA_EXCEEDED',
  'RESERVATION_EXPIRED',
  'RESERVATION_SIZE_MISMATCH',
  'BASE_PLAN_REQUIRED',
  'BILLING_CHANNEL_LOCKED',
  'CADENCE_LOCKED',
  'BOX_LIMIT_REACHED',
  'CONTENT_DOES_NOT_FIT',
  'GRANT_IN_RECOVERY',
]);
export type StorageErrorCode = z.infer<typeof StorageErrorCode>;
